import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAccess } from '../App';
import { getPitchContent, trackEvent, submitEmailSignup } from '../utils/api';
import { 
  ChevronLeft, ChevronRight, Menu, X, MessageSquare, 
  Mail, Briefcase, Users, Heart, Armchair, DollarSign,
  TrendingUp, Target, Trophy, Rocket, Building2, Banknote
} from 'lucide-react';

// Section Components
import HeroSection from '../sections/HeroSection';
import ProblemSection from '../sections/ProblemSection';
import SolutionSection from '../sections/SolutionSection';
import MarketSection from '../sections/MarketSection';
import BusinessModelSection from '../sections/BusinessModelSection';
import TractionSection from '../sections/TractionSection';
import CompetitionSection from '../sections/CompetitionSection';
import FinancialsSection from '../sections/FinancialsSection';
import TeamSection from '../sections/TeamSection';
import AskSection from '../sections/AskSection';
import AIChat from '../components/AIChat';
import EmailCapture from '../components/EmailCapture';

const SECTIONS = [
  { id: 'hero', title: 'KoreFolio', icon: Rocket, component: HeroSection },
  { id: 'problem', title: 'Problem', icon: Target, component: ProblemSection },
  { id: 'solution', title: 'Solution', icon: Briefcase, component: SolutionSection },
  { id: 'market', title: 'Market', icon: TrendingUp, component: MarketSection },
  { id: 'business-model', title: 'Business Model', icon: DollarSign, component: BusinessModelSection },
  { id: 'traction', title: 'Traction', icon: Trophy, component: TractionSection },
  { id: 'competition', title: 'Competition', icon: Users, component: CompetitionSection },
  { id: 'financials', title: 'Financials', icon: TrendingUp, component: FinancialsSection },
  { id: 'team', title: 'Team', icon: Building2, component: TeamSection },
  { id: 'ask', title: 'The Ask', icon: Banknote, component: AskSection },
];

function PitchDeck() {
  const { accessCode, accessName, sessionId, revokeAccess } = useAccess();
  const [content, setContent] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  // Load content
  useEffect(() => {
    const loadContent = async () => {
      try {
        const data = await getPitchContent();
        setContent(data);
        await trackEvent('page_view', { section: 'hero' }, sessionId, accessCode);
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [sessionId, accessCode]);

  // Track section views
  useEffect(() => {
    if (!loading && SECTIONS[currentSection]) {
      trackEvent('section_view', { 
        section: SECTIONS[currentSection].id,
        index: currentSection 
      }, sessionId, accessCode);
    }
  }, [currentSection, loading, sessionId, accessCode]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (chatOpen || emailOpen) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSection, chatOpen, emailOpen]);

  const goNext = () => {
    if (currentSection < SECTIONS.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goPrev = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const goToSection = (index) => {
    setCurrentSection(index);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-dark">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-12 h-12 border-4 border-kore-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const CurrentSectionComponent = SECTIONS[currentSection].component;
  const sectionContent = content?.sections?.find(s => s.id === SECTIONS[currentSection].id);

  return (
    <div ref={containerRef} className="min-h-screen bg-surface-dark relative overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface-dark/80 backdrop-blur-xl border-b border-surface-border">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="hidden md:flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-kore-500 to-kore-700 flex items-center justify-center">
                <span className="text-sm font-bold">K</span>
              </div>
              <span className="font-semibold">KoreFolio</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <span className="hidden md:inline">Viewing as:</span>
            <span className="text-kore-400 font-medium">{accessName || accessCode}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setChatOpen(true)}
              className="flex items-center gap-2 px-3 py-2 bg-kore-500/10 hover:bg-kore-500/20 text-kore-400 rounded-lg transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden md:inline">Ask AI</span>
            </button>
            <button
              onClick={() => setEmailOpen(true)}
              className="flex items-center gap-2 px-3 py-2 bg-accent-mint/10 hover:bg-accent-mint/20 text-accent-mint rounded-lg transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline">Stay Updated</span>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-surface-border">
          <motion.div
            className="h-full bg-gradient-to-r from-kore-500 to-accent-lavender"
            initial={{ width: 0 }}
            animate={{ width: `${((currentSection + 1) / SECTIONS.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </header>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-surface-card border-r border-surface-border z-50 overflow-y-auto"
            >
              <div className="p-4 border-b border-surface-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-kore-500 to-kore-700 flex items-center justify-center">
                    <span className="text-lg font-bold">K</span>
                  </div>
                  <div>
                    <h2 className="font-semibold">KoreFolio</h2>
                    <p className="text-xs text-zinc-500">Pitch Deck</p>
                  </div>
                </div>
              </div>

              <nav className="p-2">
                {SECTIONS.map((section, index) => {
                  const Icon = section.icon;
                  const isActive = index === currentSection;
                  const isComplete = index < currentSection;

                  return (
                    <button
                      key={section.id}
                      onClick={() => goToSection(index)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                        isActive 
                          ? 'bg-kore-500/20 text-kore-400' 
                          : 'hover:bg-white/5 text-zinc-400 hover:text-white'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isActive ? 'bg-kore-500' : isComplete ? 'bg-accent-mint/20' : 'bg-surface-elevated'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{section.title}</span>
                      {isActive && (
                        <div className="ml-auto w-2 h-2 rounded-full bg-kore-500" />
                      )}
                    </button>
                  );
                })}
              </nav>

              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-surface-border bg-surface-card">
                <button
                  onClick={revokeAccess}
                  className="w-full text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  Exit Presentation
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-20 min-h-screen">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSection}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="min-h-[calc(100vh-5rem)]"
          >
            <CurrentSectionComponent 
              content={sectionContent?.content || {}} 
              company={content?.company}
            />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Navigation */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-glass rounded-full px-6 py-3 z-30">
        <button
          onClick={goPrev}
          disabled={currentSection === 0}
          className="p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2">
          {SECTIONS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSection(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSection 
                  ? 'w-6 bg-kore-500' 
                  : index < currentSection
                    ? 'bg-accent-mint'
                    : 'bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          disabled={currentSection === SECTIONS.length - 1}
          className="p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* AI Chat Modal */}
      <AnimatePresence>
        {chatOpen && (
          <AIChat onClose={() => setChatOpen(false)} />
        )}
      </AnimatePresence>

      {/* Email Capture Modal */}
      <AnimatePresence>
        {emailOpen && (
          <EmailCapture onClose={() => setEmailOpen(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

export default PitchDeck;
